import type { LearningProfile } from './learning';
import type { PublishedOffer } from './published-offer';
import { apiGet, apiPost, apiPut } from '../../../adapters/api-client/api';
import {
  BillingModel,
  CourseSchedule,
  DeliveryFormat,
  DurationMode,
  OfferMode,
  ScheduleType,
  CourseLanguage,
} from '../domain/catalog';

/**
 * The content a family reads before enrolling (ADR-007 S4.3).
 *
 * Every field is optional and stays absent rather than becoming ''. A course
 * written before these existed carries none of them, and the detail page draws
 * a section only for what is there — a heading over an empty body reads as a
 * section the teacher failed to fill in.
 */
export interface CourseContent {
  /** ISO 639-1, one of the languages the platform teaches in (TCH-06-FR-11). */
  language?: CourseLanguage;
  overview?: string;
  learningObjectives?: string[];
  classStructure?: string;
  whatToExpect?: string;
  // Learning Outside the Class was here. TCH-06-FR-25 is "Learning Outside the
  // Class — Removed"; UC-T04 v2.0, UC-P04's PRT-BR-P04-04 and UC-S04's
  // STU-S04-02 all state it is not a Course field. Rows written before this
  // may still carry the attribute; nothing asks for it.
  // Homework was here. DEC-S2-S04-03: no course-level field; practice is per session (T10).
}

export interface SearchFilters {
  subject?: string;
  level?: string;
  minPrice?: number;
  maxPrice?: number;
  sortBy?: 'soonest' | 'newest' | 'price_low' | 'price_high';
  limit?: number;
  nextToken?: string;
}

export type { PublishedOffer };

export interface CourseSearchResult {
  listingId: string;
  /** DEC-S2-S04-01: what this course sells now. Null means it is not requestable. */
  offer: PublishedOffer | null;
  /**
   * DEC-S2-S03-03: the soonest meeting a seat can be had for. Null when none can
   * be vouched for; read `seatsLeft` and `scheduleType` for why.
   */
  nextAvailableAt: string | null;
  /** IANA zone of that meeting. */
  timezone: string | null;
  /** Null when it could not be read, which is not 0. */
  seatsLeft: number | null;
  title: string;
  subject: string;
  gradeLevel: string[];
  duration: number;
  maxStudents: number;
  price: number;
  /** What the price is denominated in. */
  currency: string;
  teacherId: string;
  /** DEC-S2-S05-02: derived from current eligibility, never a stored flag. */
  teacherVerified: boolean;
  /**
   * DEC-S2-S03-05: the course's own rating. Null when it could not be read,
   * which is not the same as `count: 0`, no reviews yet.
   */
  rating: { average: number; count: number } | null;
  offerMode?: OfferMode;
  scheduleType?: ScheduleType;
  schedule?: CourseSchedule;
  durationMode?: DurationMode;
  durationWeeks?: number;
  /** The catalog card names the language; the rest of the content is on the detail page. */
  language?: CourseLanguage;
  createdAt: string;
}

export interface SearchResponse {
  items: CourseSearchResult[];
  nextToken?: string;
  count: number;
}

/**
 * The teacher card on a course — the same approved projection the teacher's
 * profile page shows (PRT-BR-P05-19). No trust score: D-P05-02.
 */
export interface TeacherInfo {
  teacherId: string;
  firstName: string;
  lastName: string;
  bio: string;
  subjects: string[];
  /** DEC-S2-S05-02: derived from current eligibility, never a stored flag. */
  verified: boolean;
}

/** One materialized meeting (DEC-S2-S04-04). Instants are UTC; `timezone` is IANA. */
export interface OfferOccurrence {
  occurrenceId: string;
  startsAt: string;
  endsAt: string;
  timezone: string;
  durationMinutes: number;
}

export interface OfferMeetings {
  occurrences: OfferOccurrence[];
  meetingCount: number;
  /** Null without a fixed timetable. */
  meetingsPerWeek: number | null;
  /** Null when it could not be read, which is not 0. */
  seatsLeft: number | null;
}

export interface CourseDetailResponse extends CourseContent {
  listingId: string;
  /** DEC-S2-S04-01: what this course sells now. Null means it is not requestable. */
  offer: PublishedOffer | null;
  /** DEC-S2-S04-04: null means the meetings could not be read, not that there are none. */
  meetings: OfferMeetings | null;
  title: string;
  description: string;
  subject: string;
  gradeLevel: string[];
  duration: number;
  maxStudents: number;
  price: number;
  /** What the price is denominated in. */
  currency: string;
  status: string;
  offerMode?: OfferMode;
  deliveryFormat?: DeliveryFormat;
  scheduleType?: ScheduleType;
  schedule?: CourseSchedule;
  durationMode?: DurationMode;
  durationWeeks?: number;
  teacher: TeacherInfo | null;
  createdAt: string;
  updatedAt: string;
}

export function searchCourses(filters: SearchFilters = {}): Promise<SearchResponse> {
  const params = new URLSearchParams();
  if (filters.subject) params.set('subject', filters.subject);
  if (filters.level) params.set('level', filters.level);
  if (filters.minPrice !== undefined) params.set('minPrice', String(filters.minPrice));
  if (filters.maxPrice !== undefined) params.set('maxPrice', String(filters.maxPrice));
  if (filters.sortBy) params.set('sortBy', filters.sortBy);
  if (filters.limit) params.set('limit', String(filters.limit));
  if (filters.nextToken) params.set('nextToken', filters.nextToken);

  const query = params.toString();
  // Public: the endpoint is @Public() and a visitor has no token to send.
  return apiGet<SearchResponse>(`/api/v1/search/courses${query ? `?${query}` : ''}`, false);
}

export function getCourseDetail(courseId: string): Promise<CourseDetailResponse> {
  return apiGet<CourseDetailResponse>(`/api/v1/search/courses/${courseId}`, false);
}

// ── Teacher-owned course management ──────────────────────────────────────────

export type CourseStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';

/**
 * Where a course stands with the reviewer — UC-A08 (AD-A08-04).
 *
 * A second axis beside `status`, not a replacement for it: `status` says
 * whether the course is live, this says what review decided. An APPROVED
 * course can still be a DRAFT when another gate held publication back
 * (AD-A08-05). Absent on a course nobody has submitted, and on courses that
 * were already live before review existed — those were grandfathered.
 */
export type CourseReviewStatus =
  | 'DRAFT'
  | 'PENDING_REVIEW'
  | 'CHANGES_REQUIRED'
  | 'APPROVED'
  | 'REJECTED';

/**
 * A course as returned by GET /courses/my-courses.
 *
 * Note the field name: the listing aggregate stores the subject under
 * `category`, while the create/update DTOs accept it as `subject`. The
 * controller maps between them, so reads and writes differ here on purpose.
 */
export interface MyCourse extends CourseContent {
  listingId: string;
  sellerId: string;
  title: string;
  description: string;
  category: string;
  gradeLevel: string[];
  duration: number;
  maxStudents: number;
  price: number;
  /** What the price is denominated in. */
  currency: string;
  status: CourseStatus;
  /** The review axis. Absent = never submitted. */
  reviewStatus?: CourseReviewStatus;
  /** What the reviewer asked to change, or why it was rejected. Only the owner sees it. */
  reviewFeedback?: string;
  /** Ordinary resubmissions already made after changes were requested. */
  resubmissionCount?: number;
  /** When the version under review was sent. */
  submittedAt?: string;
  /** The frozen version the reviewer reads, `<courseId>:v<n>`. */
  submittedVersionId?: string;
  trustScore: number;
  offerMode: OfferMode;
  deliveryFormat: DeliveryFormat;
  billingModel: BillingModel;
  scheduleType: ScheduleType;
  schedule?: CourseSchedule;
  durationMode: DurationMode;
  durationWeeks?: number;
  /** LEM-01. Absent on a course saved before it existed: no profile (never LEP_2). */
  learningProfile?: LearningProfile;
  createdAt: string;
  updatedAt: string;
}

/** Payload accepted by POST /courses and PUT /courses/:id. */
export interface CourseInput extends CourseContent {
  title: string;
  description: string;
  subject: string;
  gradeLevel: string[];
  duration: number;
  maxStudents: number;
  price: number;
  /** What the price is denominated in. */
  currency?: string;
  offerMode: OfferMode;
  scheduleType: ScheduleType;
  /** Sent only when scheduleType is RECURRING. */
  schedule?: CourseSchedule;
  durationMode: DurationMode;
  /** Sent only when durationMode is FIXED. */
  durationWeeks?: number;
  /** LEM-01: what the family is promised about learning. */
  learningProfile?: LearningProfile;
}

export function createCourse(data: CourseInput): Promise<{ message: string; listing: MyCourse }> {
  return apiPost('/api/v1/courses', data);
}

/** Only DRAFT courses are editable server-side. */
export function updateCourse(
  courseId: string,
  data: CourseInput,
): Promise<{ message: string; listing: MyCourse }> {
  return apiPut(`/api/v1/courses/${courseId}`, data);
}

/**
 * Sends a draft — or a course returned for changes — to review (UC-A08,
 * TCH-06-FR-33). It does not publish: the course goes live only when a
 * reviewer approves the exact version sent. Idempotent server-side.
 */
export function submitCourseForReview(
  courseId: string,
): Promise<{ message: string; listing: MyCourse }> {
  return apiPost(`/api/v1/courses/${courseId}/submit`, {});
}

/**
 * Publishes a course a reviewer already APPROVED whose first publication was
 * held back by another gate. The API refuses anything not approved, and still
 * rejects unverified teachers.
 */
export function publishCourse(courseId: string): Promise<any> {
  return apiPost(`/api/v1/courses/${courseId}/publish`, {});
}

/** Moves a PUBLISHED course back to DRAFT so it can be edited. */
export function unpublishCourse(courseId: string): Promise<{ message: string }> {
  return apiPost(`/api/v1/courses/${courseId}/unpublish`, {});
}

export function archiveCourse(courseId: string): Promise<{ message: string }> {
  return apiPost(`/api/v1/courses/${courseId}/archive`, {});
}

export function getMyCourses(): Promise<{ courses: MyCourse[] }> {
  return apiGet('/api/v1/courses/my-courses');
}
